/**
 * Category configuration for icons and colors
 */

interface CategoryConfig {
  icon: string
  color: string
}

const categoryMap: Record<string, CategoryConfig> = {
  // Food & Dining
  'Food': { icon: '🛒', color: 'bg-chart-3/10 text-chart-3' },
  'Food & Dining': { icon: '🛒', color: 'bg-chart-3/10 text-chart-3' },
  'Groceries': { icon: '🛒', color: 'bg-chart-3/10 text-chart-3' },
  'Restaurant': { icon: '🍽️', color: 'bg-chart-3/10 text-chart-3' },
  'Coffee': { icon: '☕', color: 'bg-chart-3/10 text-chart-3' },

  // Transportation
  'Transport': { icon: '🚗', color: 'bg-chart-2/10 text-chart-2' },
  'Transportation': { icon: '🚗', color: 'bg-chart-2/10 text-chart-2' },
  'Car': { icon: '🚗', color: 'bg-chart-2/10 text-chart-2' },
  'Uber': { icon: '🚗', color: 'bg-chart-2/10 text-chart-2' },
  'Gas': { icon: '⛽', color: 'bg-chart-2/10 text-chart-2' },

  // Entertainment
  'Entertainment': { icon: '🎬', color: 'bg-chart-5/10 text-chart-5' },
  'Movies': { icon: '🎬', color: 'bg-chart-5/10 text-chart-5' },
  'Streaming': { icon: '🎬', color: 'bg-chart-5/10 text-chart-5' },
  'Netflix': { icon: '🎬', color: 'bg-chart-5/10 text-chart-5' },
  'Games': { icon: '🎮', color: 'bg-chart-5/10 text-chart-5' },

  // Shopping
  'Shopping': { icon: '🛍️', color: 'bg-chart-1/10 text-chart-1' },
  'Retail': { icon: '🛍️', color: 'bg-chart-1/10 text-chart-1' },
  'Clothing': { icon: '👕', color: 'bg-chart-1/10 text-chart-1' },

  // Health & Fitness
  'Health': { icon: '💪', color: 'bg-chart-4/10 text-chart-4' },
  'Fitness': { icon: '💪', color: 'bg-chart-4/10 text-chart-4' },
  'Gym': { icon: '💪', color: 'bg-chart-4/10 text-chart-4' },
  'Medical': { icon: '🏥', color: 'bg-chart-4/10 text-chart-4' },
  'Pharmacy': { icon: '💊', color: 'bg-chart-4/10 text-chart-4' },

  // Bills & Utilities
  'Bills': { icon: '📄', color: 'bg-amber-500/10 text-amber-600' },
  'Utilities': { icon: '⚡', color: 'bg-amber-500/10 text-amber-600' },
  'Rent': { icon: '🏠', color: 'bg-amber-500/10 text-amber-600' },
  'Internet': { icon: '🌐', color: 'bg-amber-500/10 text-amber-600' },
  'Phone': { icon: '📱', color: 'bg-amber-500/10 text-amber-600' },

  // Income
  'Income': { icon: '💰', color: 'bg-success/10 text-success' },
  'Salary': { icon: '💰', color: 'bg-success/10 text-success' },
  'Freelance': { icon: '💼', color: 'bg-success/10 text-success' },
  'Investment': { icon: '📈', color: 'bg-success/10 text-success' },
  'Bonus': { icon: '🎁', color: 'bg-success/10 text-success' },

  // Education
  'Education': { icon: '📚', color: 'bg-blue-500/10 text-blue-600' },
  'Books': { icon: '📖', color: 'bg-blue-500/10 text-blue-600' },
  'Courses': { icon: '🎓', color: 'bg-blue-500/10 text-blue-600' },

  // Travel
  'Travel': { icon: '✈️', color: 'bg-purple-500/10 text-purple-600' },
  'Hotel': { icon: '🏨', color: 'bg-purple-500/10 text-purple-600' },
  'Vacation': { icon: '🏖️', color: 'bg-purple-500/10 text-purple-600' },

  // Misc
  'Other': { icon: '📌', color: 'bg-gray-500/10 text-gray-600' },
  'Miscellaneous': { icon: '📌', color: 'bg-gray-500/10 text-gray-600' },
}

/**
 * Get icon for a category
 */
export function getCategoryIcon(category: string): string {
  const normalized = category.trim()

  // Try exact match first
  if (categoryMap[normalized]) {
    return categoryMap[normalized].icon
  }

  // Try case-insensitive match
  const key = Object.keys(categoryMap).find(
    k => k.toLowerCase() === normalized.toLowerCase()
  )

  if (key) {
    return categoryMap[key].icon
  }

  // Default icon
  return '📌'
}

/**
 * Get color for a category
 */
export function getCategoryColor(category: string): string {
  const normalized = category.trim()

  // Try exact match first
  if (categoryMap[normalized]) {
    return categoryMap[normalized].color
  }

  // Try case-insensitive match
  const key = Object.keys(categoryMap).find(
    k => k.toLowerCase() === normalized.toLowerCase()
  )

  if (key) {
    return categoryMap[key].color
  }

  // Default color
  return 'bg-gray-500/10 text-gray-600'
}

/**
 * Get all available categories
 */
export function getAllCategories(): string[] {
  return Object.keys(categoryMap)
}
